import { FC } from "react";
import { Controller } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Input from "../../components/Input";
import Checkbox from "../../components/Checkbox";
import { Button, ButtonType } from "../../components/Button";
import { t } from "i18next";
import { useLeagueFilterForm, LeagueFilterSharedProps } from "./useLeagueFilterForm";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

export type LeagueFilterFormProps = LeagueFilterSharedProps & {
  onClose?: () => void;
};

const LeagueFilterForm: FC<LeagueFilterFormProps> = ({
  onClose,
  ...shared
}) => {
  const {
    control,
    handleSubmit,
    onSubmit,
    search,
    handleSearchChange,
    filteredOptions,
    isAllSelected,
    selectedLeaguesNo,
    length,
    handleToggleAll,
    handleToggleLeague,
  } = useLeagueFilterForm(shared);

  return (
    <form className="flex flex-col min-h-full" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex py-4 justify-between items-center">
        <h1 className="font-bold text-2xl">{t("League")}</h1>
        <button type="button" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>
      <div className="p-3 border-gray-300 border-t border-x rounded-t-md flex items-center justify-between">
        <Input
          startAdornment={<SearchIcon className="absolute left-0 top-2" />}
          className="w-6/12 pl-8 relative"
          placeholder={t("Search") as string}
          onChange={handleSearchChange}
          value={search}
        />
        <div>
          <Controller
            name="isAllSelected"
            control={control}
            render={({ field }) => (
              <label className="cursor-pointer">
                <Checkbox
                  checked={field.value}
                  onChange={(event) =>
                    handleToggleAll(
                      (event.target as HTMLInputElement).checked,
                      field.onChange
                    )
                  }
                />
                {t("Select / Cancel all")}
              </label>
            )}
          />
        </div>

        <div>
          {selectedLeaguesNo}/{length} {t("selected")}
        </div>
      </div>
      <Controller
        name="selectedLeagues"
        control={control}
        render={({ field }) => (
          <div className="flex flex-wrap overflow-auto border-gray-300 border rounded-b-md">
            {filteredOptions.length === 0 && (
              <div className="p-8 text-center w-full text-gray-400 text-xl">
                {t("League is not found")}
              </div>
            )}
            {filteredOptions.map((data) => {
              const leagueId = data.value;
              const isChecked = field.value.includes(leagueId);

              return (
                <div key={data.value} className="w-6/12 p-3">
                  <label className="cursor-pointer flex items-center gap-x-2">
                    <Checkbox
                      checked={isAllSelected ? !isChecked : isChecked}
                      onChange={() =>
                        handleToggleLeague(
                          leagueId,
                          isChecked,
                          field.onChange,
                          field.value
                        )
                      }
                    />
                    <span>{data.label}</span>
                    {data.favorite && (
                      <StarSolidIcon className="w-5 h-5 text-yellow-400" />
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      />

      <div className="py-4 flex space-x-5 justify-center">
        <Button className="px-2" type="submit" buttonType={ButtonType.PRIMARY}>
          {t("Confirm")}
        </Button>
        <Button type="button" className="px-2" onClick={onClose}>
          {t("Cancel")}
        </Button>
      </div>
    </form>
  );
};

export default LeagueFilterForm;
